import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {formatDay, formatLocalTime} from '@common/utils/dates'

import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Button} from '~/components/general/button/Button'
import {Icon} from '~/components/general/icon/Icon'
import {SimpleTable} from '~/components/general/table/SimpleTable'
import {TableCellIconButtonPopup} from '~/components/patients/common/patientsTable/PatientsTable.styled'
import {theme} from '~/styles/theme'
import {useGetPartnerAdminPractitionersWorkingHours} from '~/api/partnerAdminPractitionersWorkingHours'
import {useDeletePractitionerWorkingHour} from '~/api/practitionerWorkingHours'
import {StaffWorkingHoursModal} from '~/components/partnerAdmin/staff/StaffWorkingHoursModal'

// moved to ~/utils/datetime

type RowActionMenuProps = {
  id: string
  practitionerId: string
  onEdit: () => void
}

const RowActionMenu: React.FC<RowActionMenuProps> = ({
  id,
  practitionerId,
  onEdit,
}) => {
  const {t} = useTranslation<string>()
  const {mutate: deleteWorkingHour} = useDeletePractitionerWorkingHour({
    id,
    practitionerId,
  })

  const menuItems = [
    {
      label: t('Edit'),
      onClick: onEdit,
    },
    {
      label: t('Delete'),
      onClick: () => {
        if (window.confirm(t('Are you sure you want to delete this item?'))) {
          deleteWorkingHour(undefined)
        }
      },
    },
  ]

  return <TableCellIconButtonPopup menuItems={menuItems} />
}

export const WorkingHoursPage: React.FC = () => {
  const {t} = useTranslation<string>()
  const {data, isLoading} = useGetPartnerAdminPractitionersWorkingHours()
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const editingItem = useMemo(
    () => data?.find((item) => item.id === editingId) || null,
    [data, editingId],
  )

  const headerData = useMemo(
    () => ({
      columns: [
        {title: t('Staff'), columnWidthPercent: 30},
        {title: t('Day'), columnWidthPercent: 30},
        {title: t('Start time'), columnWidthPercent: 20},
        {title: t('End time'), columnWidthPercent: 10},
        {title: '', columnWidthPercent: 10},
      ],
    }),
    [t],
  )

  const rows = useMemo(
    () =>
      (data || []).map((item) => ({
        columns: [
          `${item.practitionerFirstName} ${item.practitionerSecondName}`.trim(),
          formatDay(item.dayOfWeek),
          formatLocalTime(item.startTime),
          formatLocalTime(item.endTime),
        ],
        actionComponent: (
          <RowActionMenu
            id={item.id}
            practitionerId={item.practitionerId}
            onEdit={() => {
              setEditingId(item.id)
              setShowModal(true)
            }}
          />
        ),
      })),
    [data],
  )

  return (
    <Container flex direction="column" spacing={4}>
      <Container flex justify="space-between" align="center">
        <Typography size="lg" weight="medium">
          {t('Working hours')}
        </Typography>
        <Button
          size="sm"
          startIcon={<Icon name="Plus" color={theme.colors.white} size="sm" />}
          onClick={() => {
            setEditingId(null)
            setShowModal(true)
          }}>
          {t('Add working hour')}
        </Button>
      </Container>

      <SimpleTable header={headerData} rows={rows} isLoading={isLoading} />

      <StaffWorkingHoursModal
        isVisible={showModal}
        onClose={() => setShowModal(false)}
        workingHourSummary={editingItem}
      />
    </Container>
  )
}
